import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {BuilderTransferSourceForm} from './BuilderTransferSourceForm';
import {TransferSourceSubplan} from './TransferSourceSubplan';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';

interface TransferSourceParentProps {
  pot: BuilderPot;
  selectedSourceIds: string[];
  greyed?: boolean;
  sameReview?: boolean;
  handleChange: (id: string) => void;
}

export const TransferSourceParent: React.FC<TransferSourceParentProps> = ({
  pot,
  selectedSourceIds,
  greyed,
  sameReview,
  handleChange,
}) => {
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = dentistChildren.length > 0;
  const isLeafSelected = !hasChildren && selectedSourceIds.includes(pot.id);
  const anyChildSelected =
    hasChildren && dentistChildren.some((c) => selectedSourceIds.includes(c.id));
  const activeFormikKey = isLeafSelected
    ? `transfer.transferSources.${pot.id}`
    : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  if (hasChildren) {
    const {label, currentValue} = getSummaryParts(pot);
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={anyChildSelected}
        sameReview={sameReview}
        summaryChildren={
          <Box sx={{display: 'flex', alignItems: 'center', flex: 1}}>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            {currentValue && (
              <Typography
                variant="body2"
                sx={{fontWeight: 600, ml: 'auto', whiteSpace: 'nowrap'}}
              >
                {currentValue}
              </Typography>
            )}
          </Box>
        }
      >
        {dentistChildren.map((child) => (
          <TransferSourceSubplan
            key={child.id}
            subPlan={child}
            isSelected={selectedSourceIds.includes(child.id)}
            handleChange={() => handleChange(child.id)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  return (
    <ParentPlanRow pot={pot} isSelected={isLeafSelected} sameReview={sameReview}>
      <PlanAccordionShell
        expanded={isLeafSelected}
        isSelected={isLeafSelected}
        isSoleParent
        validationLevel={bgClass}
        greyed={greyed}
        sameReview={sameReview}
        summaryChildren={
          <>
            <PlanSelector
              mode="multiple"
              id={pot.id}
              isSelected={isLeafSelected}
              handleChange={() => handleChange(pot.id)}
            />
            {getSummary(pot)}
          </>
        }
        summaryTrailing={
          activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        <BuilderTransferSourceForm
          pot={pot}
          id={pot.id}
          index={0}
          maxAmount={getCurrencyFloat(pot.totalValue)}
        />
      </PlanAccordionShell>
    </ParentPlanRow>
  );
};
